import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CLOUDFLARE_CONFIG, API_CONFIG } from '../config/api';
import apiService from "../services/apiService";

const AddPortfolio = () => {
  const navigate = useNavigate();
  const [portfolioData, setPortfolioData] = useState({
    title: '',
    description: ''
  });
  const [imageUploads, setImageUploads] = useState({
    heroImage: { file: null, status: '', link: '' },
    image1: { file: null, status: '', link: '' },
    image2: { file: null, status: '', link: '' },
    image3: { file: null, status: '', link: '' }
  });
  const [saving, setSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setPortfolioData({
      ...portfolioData,
      [field]: value
    });
  };

  const handleFileChange = (imageKey, file) => {
    setImageUploads({
      ...imageUploads,
      [imageKey]: { ...imageUploads[imageKey], file: file, status: '', link: '' }
    });
  };

  const uploadToCloudflare = async (imageKey, file) => {
    try {
      // Update status
      setImageUploads(prev => ({
        ...prev,
        [imageKey]: { ...prev[imageKey], status: 'Uploading to Cloudflare...' }
      }));

      // Generate a unique ID for the portfolio (you might want to use a proper ID generation method)
      const portfolioId = Date.now();
      
      // Rename file with CBREX prefix
      const renamedFile = new File([file], `CBREX_${portfolioId}_${file.name}`, {
        type: file.type,
      });

      // Upload to Cloudflare - try direct first, fall back to proxies if CORS blocks
      const cloudflareFormData = new FormData();
      cloudflareFormData.append("file", renamedFile);

      let cloudflareResponse = null;

      // Try direct upload first (no proxy)
      try {
        const directResponse = await fetch(CLOUDFLARE_CONFIG.IMAGES_ENDPOINT, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${CLOUDFLARE_CONFIG.API_TOKEN}`,
          },
          body: cloudflareFormData,
        });

        if (directResponse.ok) {
          cloudflareResponse = await directResponse.json();
        }
      } catch (error) {
        console.log('Direct upload failed (likely CORS), trying proxies...', error);
      }

      // Only use proxies if direct upload failed
      if (!cloudflareResponse) {
        for (const proxy of API_CONFIG.CORS_PROXIES) {
          try {
            const response = await fetch(
              proxy + CLOUDFLARE_CONFIG.IMAGES_ENDPOINT,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${CLOUDFLARE_CONFIG.API_TOKEN}`,
                },
                body: cloudflareFormData,
              }
            );

            if (response.ok) {
              cloudflareResponse = await response.json();
              break;
            }
          } catch (error) {
            console.log(`Proxy ${proxy} failed:`, error);
            continue;
          }
        }
      }

      if (!cloudflareResponse) {
        throw new Error("All CORS proxies failed");
      }

      if (cloudflareResponse.success && cloudflareResponse.result.variants) {
        const imageLink = cloudflareResponse.result.variants[0];
        
        setImageUploads(prev => ({
          ...prev,
          [imageKey]: { 
            ...prev[imageKey], 
            status: 'Upload successful!', 
            link: imageLink 
          }
        }));

        return imageLink;
      } else {
        throw new Error("Cloudflare upload failed");
      }
    } catch (error) {
      console.error(`Error uploading ${imageKey}:`, error);
      setImageUploads(prev => ({
        ...prev,
        [imageKey]: { ...prev[imageKey], status: 'Upload failed!' }
      }));
      throw error;
    }
  };

  const handleImageUpload = async (imageKey) => {
    const file = imageUploads[imageKey].file;
    if (!file) return;

    try {
      await uploadToCloudflare(imageKey, file);
    } catch (error) {
      console.error(`Failed to upload ${imageKey}:`, error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Check if all required fields are filled
      if (!portfolioData.title || !portfolioData.description) {
        alert('Please fill in title and description');
        return;
      }

      // Upload all images first and collect their links
      const imageLinks = {
        heroImageLink: '',
        image1Link: '',
        image2Link: '',
        image3Link: ''
      };

      // Upload images one by one and collect their links
      if (imageUploads.heroImage.file && !imageUploads.heroImage.link) {
        const link = await uploadToCloudflare('heroImage', imageUploads.heroImage.file);
        imageLinks.heroImageLink = link;
      } else {
        imageLinks.heroImageLink = imageUploads.heroImage.link || '';
      }

      if (imageUploads.image1.file && !imageUploads.image1.link) {
        const link = await uploadToCloudflare('image1', imageUploads.image1.file);
        imageLinks.image1Link = link;
      } else {
        imageLinks.image1Link = imageUploads.image1.link || '';
      }

      if (imageUploads.image2.file && !imageUploads.image2.link) {
        const link = await uploadToCloudflare('image2', imageUploads.image2.file);
        imageLinks.image2Link = link;
      } else {
        imageLinks.image2Link = imageUploads.image2.link || '';
      }

      if (imageUploads.image3.file && !imageUploads.image3.link) {
        const link = await uploadToCloudflare('image3', imageUploads.image3.file);
        imageLinks.image3Link = link;
      } else {
        imageLinks.image3Link = imageUploads.image3.link || '';
      }

      const requestBody = {
        userId: 3,
        title: portfolioData.title,
        description: portfolioData.description,
        heroImageLink: imageLinks.heroImageLink,
        image1Link: imageLinks.image1Link,
        image2Link: imageLinks.image2Link,
        image3Link: imageLinks.image3Link
      };

      console.log('Sending portfolio data:', requestBody); // Debug log

      const result = await apiService.post('/portfolios', requestBody);
      console.log('Portfolio created successfully:', result);
      
      alert('Portfolio created successfully!');
      
      // Navigate back to manage portfolios or clear the form
      navigate('/portfolios');
      
    } catch (err) {
      console.error('Error creating portfolio:', err);
      alert('Failed to create portfolio. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>Add Portfolio</h1>
      </div>

      <div className="content-container">
        <div className="form-section">
          <h3>Portfolio Information</h3>
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="title">Title:</label>
              <input
                type="text"
                id="title"
                className="form-input"
                value={portfolioData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
            </div>
            
            <div className="form-row">
              <label htmlFor="description">Description:</label>
              <textarea
                id="description"
                className="form-textarea"
                value={portfolioData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows="4"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Image Uploads</h3>
          
          {/* Hero Image */}
          <div className="form-row">
            <label htmlFor="heroImage">Hero Image:</label>
            <div className="upload-controls">
              <input
                type="file"
                id="heroImage"
                accept="image/*"
                onChange={(e) => handleFileChange('heroImage', e.target.files[0])}
                className="form-input"
              />
              <button
                type="button"
                onClick={() => handleImageUpload('heroImage')}
                disabled={!imageUploads.heroImage.file || imageUploads.heroImage.link}
                className="btn-primary"
                style={{ marginLeft: '10px' }}
              >
                Upload
              </button>
              {imageUploads.heroImage.status && (
                <span className="upload-status" style={{ marginLeft: '10px' }}>
                  {imageUploads.heroImage.status}
                </span>
              )}
            </div>
          </div>

          {/* Image 1 */}
          <div className="form-row">
            <label htmlFor="image1">Image 1:</label>
            <div className="upload-controls">
              <input
                type="file"
                id="image1"
                accept="image/*"
                onChange={(e) => handleFileChange('image1', e.target.files[0])}
                className="form-input"
              />
              <button
                type="button"
                onClick={() => handleImageUpload('image1')}
                disabled={!imageUploads.image1.file || imageUploads.image1.link}
                className="btn-primary"
                style={{ marginLeft: '10px' }}
              >
                Upload
              </button>
              {imageUploads.image1.status && (
                <span className="upload-status" style={{ marginLeft: '10px' }}>
                  {imageUploads.image1.status}
                </span>
              )}
            </div>
          </div>

          {/* Image 2 */}
          <div className="form-row">
            <label htmlFor="image2">Image 2:</label>
            <div className="upload-controls">
              <input
                type="file"
                id="image2"
                accept="image/*"
                onChange={(e) => handleFileChange('image2', e.target.files[0])}
                className="form-input"
              />
              <button
                type="button"
                onClick={() => handleImageUpload('image2')}
                disabled={!imageUploads.image2.file || imageUploads.image2.link}
                className="btn-primary"
                style={{ marginLeft: '10px' }}
              >
                Upload
              </button>
              {imageUploads.image2.status && (
                <span className="upload-status" style={{ marginLeft: '10px' }}>
                  {imageUploads.image2.status}
                </span>
              )}
            </div>
          </div>

          {/* Image 3 */}
          <div className="form-row">
            <label htmlFor="image3">Image 3:</label>
            <div className="upload-controls">
              <input
                type="file"
                id="image3"
                accept="image/*"
                onChange={(e) => handleFileChange('image3', e.target.files[0])}
                className="form-input"
              />
              <button
                type="button"
                onClick={() => handleImageUpload('image3')}
                disabled={!imageUploads.image3.file || imageUploads.image3.link}
                className="btn-primary"
                style={{ marginLeft: '10px' }}
              >
                Upload
              </button>
              {imageUploads.image3.status && (
                <span className="upload-status" style={{ marginLeft: '10px' }}>
                  {imageUploads.image3.status}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Creating Portfolio...' : 'Create Portfolio'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPortfolio;
